import { getStartOfRangeHorizon } from '@chart';
import { getPriceStatus } from '@helpers/utils/numbers/numbers';
import { priceChangeColors } from '@terminal/utils/common/utils';
import * as d3 from 'd3';
import { useMemo } from 'react';

import styles from './SparklineChart.module.scss';
import type { SparklineChartProps } from './SparklineChart.types';

const CHART_PADDING_PX = 2;

export const SparklineChart = ({
  data,
  dataTestId = 'sparkline-chart',
  height,
  horizon,
  ref,
  width,
  ...svgProps
}: SparklineChartProps) => {
  const [plotLine, priceStatus] = useMemo(() => {
    if (data.length < 2) {
      return [undefined, getPriceStatus()] as const;
    }

    const latestDataPoint = data[data.length - 1];
    const horizonStart = getStartOfRangeHorizon(
      horizon,
      new Date(latestDataPoint.index)
    );
    const dataWithinHorizon =
      horizonStart === undefined
        ? data
        : data.filter(item => new Date(item.index).getTime() >= horizonStart);
    const [yMin = 0, yMax = 1] = d3.extent(
      dataWithinHorizon,
      item => item.value
    );

    const xScale = d3
      .scaleLinear()
      .domain([0, dataWithinHorizon.length - 1])
      .range([CHART_PADDING_PX, width - CHART_PADDING_PX]);
    const yScale = d3
      .scaleLinear()
      .domain([yMin, yMax])
      .range([height - CHART_PADDING_PX, CHART_PADDING_PX]);
    const line = d3
      .line()
      .x(point => xScale(point[0]))
      .y(point => yScale(point[1]))
      .curve(d3.curveCardinal)(
      dataWithinHorizon.map((item, index) => [index, item.value])
    );
    const status = getPriceStatus(
      dataWithinHorizon[dataWithinHorizon.length - 1].value -
        dataWithinHorizon[0].value
    );

    return [line, status] as const;
  }, [data, height, horizon, width]);

  return (
    <svg
      {...svgProps}
      data-testid={dataTestId}
      height={height}
      ref={ref}
      width={width}
    >
      <path
        className={styles.plotPath}
        data-testid="chart-path"
        d={plotLine ?? ''}
        style={{ stroke: priceChangeColors[priceStatus] }}
      />
    </svg>
  );
};
